import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { getListCombinedPayment } from '_redux/modules/event'
import { buildCombinedPaymentData, getOrderAmount } from '../helpers/combinedPaymentHelper'

export default function useCombinedPaymentOrders(order, options = { enabled: true }) {
  const dispatch = useDispatch()
  const store = useStore()
  const [combinedOrders, setCombinedOrders] = useState([])
  const [combinedDeliveries, setCombinedDeliveries] = useState([])
  const [isLoadingCombined, setIsLoadingCombined] = useState(false)

  const combinedPaymentRef = order?.combinedPaymentRef || ''
  const isCombined = Boolean(combinedPaymentRef)
  const enabled = options?.enabled ?? true

  const fetchCombinedData = useCallback(
    async (targetOrder) => {
      const currentOrder = targetOrder || order
      const ref = currentOrder?.combinedPaymentRef
      if (!ref) {
        return { listCombinedOrder: [], listCombinedDelivery: [] }
      }

      try {
        setIsLoadingCombined(true)
        await dispatch(getListCombinedPayment([ref]))
        const { eventAndOrderById, listCombinedPayments } = store.getState().event
        const combinedData = buildCombinedPaymentData(
          currentOrder,
          eventAndOrderById,
          listCombinedPayments
        )
        setCombinedOrders(combinedData.listCombinedOrder)
        setCombinedDeliveries(combinedData.listCombinedDelivery)
        return combinedData
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load combined payment data:', error)
        return { listCombinedOrder: [], listCombinedDelivery: [] }
      } finally {
        setIsLoadingCombined(false)
      }
    },
    [order, dispatch, store]
  )

  useEffect(() => {
    if (!enabled || !combinedPaymentRef) {
      if (order?.listCombinedOrder && order.listCombinedOrder.length > 0) {
        setCombinedOrders(order.listCombinedOrder)
      } else {
        setCombinedOrders([])
      }
      return
    }

    if (order?.listCombinedOrder && order.listCombinedOrder.length > 0) {
      setCombinedOrders(order.listCombinedOrder)
      if (order?.listCombinedDelivery) {
        setCombinedDeliveries(order.listCombinedDelivery)
      }
      return
    }

    fetchCombinedData(order)
  }, [combinedPaymentRef, order?.ref, enabled, fetchCombinedData])

  const totalCombinedAmount = useMemo(() => {
    if (!combinedOrders || !combinedOrders.length) return 0
    return combinedOrders.reduce((sum, o) => sum + getOrderAmount(o), 0)
  }, [combinedOrders])

  const hitpaySummary = useMemo(() => {
    const rawHitpayAmount =
      order?.hitpayAmount ??
      combinedOrders?.find((o) => o?.hitpayAmount != null)?.hitpayAmount
    const commissionPercentage =
      order?.hitpayPlatformCommissionPercentage ??
      combinedOrders?.find((o) => o?.hitpayPlatformCommissionPercentage != null)
        ?.hitpayPlatformCommissionPercentage
    const commissionAmount =
      order?.hitpayPlatformCommissionAmount ??
      combinedOrders?.find((o) => o?.hitpayPlatformCommissionAmount != null)
        ?.hitpayPlatformCommissionAmount

    if (commissionAmount === null || commissionAmount === undefined) {
      return null
    }

    const baseAmount =
      rawHitpayAmount != null
        ? Number(rawHitpayAmount)
        : totalCombinedAmount > 0
          ? totalCombinedAmount
          : Number(order?.totalFee) || 0
    const feeAmount = Number(commissionAmount) || 0
    const netAmount = baseAmount - feeAmount

    return {
      hitpayAmount: baseAmount,
      commissionPercentage,
      commissionAmount: feeAmount,
      netAmount,
    }
  }, [order, combinedOrders, totalCombinedAmount])

  return {
    combinedOrders,
    combinedDeliveries,
    isLoadingCombined,
    totalCombinedAmount,
    isCombined,
    fetchCombinedData,
    hitpaySummary,
  }
}
